import type Phaser from 'phaser';
import type { MvpCharacterId } from '../session/game-session';
import { CHARACTER_CARD_DATA, CHARACTER_SELECT_LAYOUT } from './character-select-layout';
import { StickPreviewRenderer } from './stick-preview-renderer';
import { CHARACTER_SKILL_LABELS } from '../ui/ui-labels';

export type CardPlayerStatus =
  | '1P_SELECTING'
  | '1P_LOCKED'
  | '2P_SELECTING'
  | '2P_LOCKED'
  | '1P_LOCKED_2P_SELECTING'
  | 'BOTH_LOCKED'
  | 'CPU'
  | 'UNSELECTED';

export interface CardRenderState {
  readonly status: CardPlayerStatus;
  readonly isPrimary: boolean;
}

export class CharacterCardComponent {
  public readonly container: Phaser.GameObjects.Container;
  private readonly bgGraphics: Phaser.GameObjects.Graphics;
  private readonly previewGraphics: Phaser.GameObjects.Graphics;
  private readonly badgeBg: Phaser.GameObjects.Graphics;
  private readonly badgeText: Phaser.GameObjects.Text;
  private readonly characterId: MvpCharacterId;
  private isHovered = false;
  private currentState: CardRenderState = { status: 'UNSELECTED', isPrimary: false };

  public constructor(
    scene: Phaser.Scene,
    characterId: MvpCharacterId,
    centerX: number,
    centerY: number,
    onSelect: (id: MvpCharacterId) => void,
  ) {
    this.characterId = characterId;
    const cardData = CHARACTER_CARD_DATA[characterId];
    const skillLabels = CHARACTER_SKILL_LABELS[characterId];
    const layout = CHARACTER_SELECT_LAYOUT;

    this.container = scene.add.container(centerX, centerY);

    // Card background and border graphics
    this.bgGraphics = scene.add.graphics();

    // Department Header (Corporate Personnel Card style)
    const deptText = scene.add.text(0, layout.cardOffsets.department.y, cardData.department, {
      fontFamily: '"Helvetica Neue", "Segoe UI", "Hiragino Kaku Gothic ProN", "Meiryo", system-ui, sans-serif',
      fontSize: '15px',
      fontStyle: 'bold',
      color: cardData.departmentColor,
      align: 'center',
    }).setOrigin(0.5);

    // Character Name
    const nameText = scene.add.text(0, layout.cardOffsets.name.y, cardData.characterName, {
      fontFamily: '"Helvetica Neue", "Segoe UI", "Hiragino Kaku Gothic ProN", "Meiryo", system-ui, sans-serif',
      fontSize: '26px',
      fontStyle: 'bold',
      color: '#f8fafc',
      align: 'center',
    }).setOrigin(0.5);

    // Stick Figure Preview Graphics
    this.previewGraphics = scene.add.graphics();

    // 2-line Character Trait
    const traitText = scene.add.text(0, layout.cardOffsets.traits.y, cardData.traits.join('\n'), {
      fontFamily: '"Helvetica Neue", "Segoe UI", "Hiragino Kaku Gothic ProN", "Meiryo", system-ui, sans-serif',
      fontSize: '13px',
      color: '#94a3b8',
      align: 'center',
      lineSpacing: 3,
    }).setOrigin(0.5);

    // Skill 1
    const skill1Text = scene.add.text(0, layout.cardOffsets.skill1.y, `スキル1 : ${skillLabels.skill1}`, {
      fontFamily: '"Helvetica Neue", "Segoe UI", "Hiragino Kaku Gothic ProN", "Meiryo", system-ui, sans-serif',
      fontSize: '14px',
      color: '#cbd5e1',
      align: 'center',
    }).setOrigin(0.5);

    // Skill 2
    const skill2Text = scene.add.text(0, layout.cardOffsets.skill2.y, `スキル2 : ${skillLabels.skill2}`, {
      fontFamily: '"Helvetica Neue", "Segoe UI", "Hiragino Kaku Gothic ProN", "Meiryo", system-ui, sans-serif',
      fontSize: '14px',
      color: '#cbd5e1',
      align: 'center',
    }).setOrigin(0.5);

    // Super
    const superText = scene.add.text(0, layout.cardOffsets.super.y, `必殺技  : ${skillLabels.super}`, {
      fontFamily: '"Helvetica Neue", "Segoe UI", "Hiragino Kaku Gothic ProN", "Meiryo", system-ui, sans-serif',
      fontSize: '14px',
      fontStyle: 'bold',
      color: '#fde047',
      align: 'center',
    }).setOrigin(0.5);

    // Badge Graphics & Text
    this.badgeBg = scene.add.graphics();
    this.badgeText = scene.add.text(0, layout.cardOffsets.badge.y, '', {
      fontFamily: '"Helvetica Neue", "Segoe UI", "Hiragino Kaku Gothic ProN", "Meiryo", system-ui, sans-serif',
      fontSize: '16px',
      fontStyle: 'bold',
      color: '#ffffff',
      align: 'center',
    }).setOrigin(0.5);

    // Hit area for interaction
    const hitArea = scene.add.rectangle(0, 0, 380, 450, 0x000000, 0)
      .setInteractive({ useHandCursor: true });

    hitArea.on('pointerover', () => {
      this.isHovered = true;
      this.render();
    });
    hitArea.on('pointerout', () => {
      this.isHovered = false;
      this.render();
    });
    hitArea.on('pointerdown', () => onSelect(this.characterId));

    this.container.add([
      this.bgGraphics,
      deptText,
      nameText,
      this.previewGraphics,
      traitText,
      skill1Text,
      skill2Text,
      superText,
      this.badgeBg,
      this.badgeText,
      hitArea,
    ]);

    this.render();
  }

  public updateState(state: CardRenderState): void {
    this.currentState = state;
    this.render();
  }

  private render(): void {
    const { status, isPrimary } = this.currentState;
    const cardData = CHARACTER_CARD_DATA[this.characterId];
    const layout = CHARACTER_SELECT_LAYOUT;

    const width = 380;
    const height = 450;
    const halfW = width / 2;
    const halfH = height / 2;

    // Determine visual style based on status & hover
    let borderColor: number;
    let borderWidth: number;
    let bgAlpha: number;
    let playerSlot: 'p1' | 'p2' | 'both' | 'cpu' | 'none';
    let badgeLabel: string;
    let badgeColor: string;
    let badgeBgColor: number;
    let badgeBorderColor: number;

    switch (status) {
      case '1P_SELECTING':
        borderColor = layout.colors.p1Accent;
        borderWidth = 3;
        bgAlpha = 0.96;
        playerSlot = 'p1';
        badgeLabel = '【 1P 選択中 】';
        badgeColor = '#38bdf8';
        badgeBgColor = 0x0c4a6e;
        badgeBorderColor = layout.colors.p1Accent;
        break;
      case '1P_LOCKED':
        borderColor = layout.colors.p1Accent;
        borderWidth = 2.5;
        bgAlpha = 0.94;
        playerSlot = 'p1';
        badgeLabel = '【 1P 確定 】';
        badgeColor = '#38bdf8';
        badgeBgColor = 0x0c4a6e;
        badgeBorderColor = layout.colors.p1Accent;
        break;
      case '2P_SELECTING':
        borderColor = layout.colors.p2Accent;
        borderWidth = 3;
        bgAlpha = 0.96;
        playerSlot = 'p2';
        badgeLabel = '【 2P 選択中 】';
        badgeColor = '#f97316';
        badgeBgColor = 0x431407;
        badgeBorderColor = layout.colors.p2Accent;
        break;
      case '2P_LOCKED':
        borderColor = layout.colors.p2Accent;
        borderWidth = 2.5;
        bgAlpha = 0.94;
        playerSlot = 'p2';
        badgeLabel = '【 2P 確定 】';
        badgeColor = '#f97316';
        badgeBgColor = 0x431407;
        badgeBorderColor = layout.colors.p2Accent;
        break;
      case '1P_LOCKED_2P_SELECTING':
        borderColor = layout.colors.bothAccent;
        borderWidth = 3;
        bgAlpha = 0.98;
        playerSlot = 'both';
        badgeLabel = '【 1P確定 / 2P選択中 】';
        badgeColor = '#fde047';
        badgeBgColor = 0x422006;
        badgeBorderColor = layout.colors.bothAccent;
        break;
      case 'BOTH_LOCKED':
        borderColor = layout.colors.bothAccent;
        borderWidth = 3;
        bgAlpha = 0.98;
        playerSlot = 'both';
        badgeLabel = '【 1P & 2P 確定 】';
        badgeColor = '#fde047';
        badgeBgColor = 0x422006;
        badgeBorderColor = layout.colors.bothAccent;
        break;
      case 'CPU':
        borderColor = 0x475569;
        borderWidth = 1.5;
        bgAlpha = 0.85;
        playerSlot = 'cpu';
        badgeLabel = '【 CPU 】';
        badgeColor = '#94a3b8';
        badgeBgColor = 0x1e293b;
        badgeBorderColor = 0x475569;
        break;
      case 'UNSELECTED':
      default:
        borderColor = this.isHovered ? 0x64748b : layout.colors.cardBorder;
        borderWidth = this.isHovered ? 2 : 1.5;
        bgAlpha = this.isHovered ? 0.92 : 0.82;
        playerSlot = 'none';
        badgeLabel = '【 未選択 】';
        badgeColor = '#64748b';
        badgeBgColor = 0x0f172a;
        badgeBorderColor = 0x334155;
        break;
    }

    if (this.isHovered && status === 'UNSELECTED') {
      borderColor = cardData.themeColor;
    }

    // Draw Card Background and Borders
    this.bgGraphics.clear();

    // Base card fill
    this.bgGraphics.fillStyle(0x0f172a, bgAlpha);
    this.bgGraphics.fillRoundedRect(-halfW, -halfH, width, height, 6);

    // Inner subtle card header tint
    this.bgGraphics.fillStyle(cardData.themeColor, 0.12);
    this.bgGraphics.fillRect(-halfW + 2, -halfH + 2, width - 4, 48);

    // Top department accent stripe
    this.bgGraphics.fillStyle(cardData.themeColor, 0.9);
    this.bgGraphics.fillRect(-halfW + 4, -halfH + 2, width - 8, 3.5);

    // Divider line between preview and traits
    this.bgGraphics.lineStyle(1, 0x1e293b, 0.8);
    this.bgGraphics.lineBetween(-halfW + 24, 20, halfW - 24, 20);

    // Divider line above skills
    this.bgGraphics.lineStyle(1, 0x1e293b, 0.8);
    this.bgGraphics.lineBetween(-halfW + 24, 70, halfW - 24, 70);

    // Main border
    this.bgGraphics.lineStyle(borderWidth, borderColor, 1);
    this.bgGraphics.strokeRoundedRect(-halfW, -halfH, width, height, 6);

    // Draw Preview Stick Figure
    this.previewGraphics.clear();
    StickPreviewRenderer.draw(
      this.previewGraphics,
      this.characterId,
      0,
      layout.cardOffsets.preview.y,
      { playerSlot, scale: 0.74 },
    );

    // Draw Badge
    const badgeW = Math.min(260, Math.max(160, badgeLabel.length * 15 + 30));
    const badgeH = 30;
    const badgeY = layout.cardOffsets.badge.y;

    this.badgeBg.clear();
    this.badgeBg.fillStyle(badgeBgColor, 0.92);
    this.badgeBg.fillRoundedRect(-badgeW / 2, badgeY - badgeH / 2, badgeW, badgeH, 4);
    this.badgeBg.lineStyle(1.5, badgeBorderColor, 1);
    this.badgeBg.strokeRoundedRect(-badgeW / 2, badgeY - badgeH / 2, badgeW, badgeH, 4);

    this.badgeText.setText(badgeLabel).setColor(badgeColor);

    // Slight scale for selected card
    const targetScale = isPrimary ? 1.02 : 1.0;
    this.container.setScale(targetScale);
  }
}
